import { Button, Fields, Loader } from '@ets-global/b2c-website-ui';
import { Field as BaseFormikField, Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { CIVILITIES } from '../../../graphql/queries';
import { getTranslatedUserCivility } from '../../../helpers/Formatter/UserFormatter';
import { useDataLayer } from '../../../hooks';
import useQuery from '../../../hooks/useQuery';
import useYup from '../../../hooks/useYup';

const Step1 = ({ initialValues = {}, onSubmit = () => {} }) => {
  const { t } = useTranslation();
  const { Yup } = useYup();
  const { pushEventLoadedB2BContactFunnel } = useDataLayer();

  useEffect(() => {
    pushEventLoadedB2BContactFunnel({
      stepName: 'contact information',
      stepNumber: 1,
    });
  }, []);

  const { data: queryDataCivility, loading: loadingCivility } = useQuery(CIVILITIES);

  let civilities = queryDataCivility.civilities || [];
  civilities = civilities.map(({ id }) => ({ label: getTranslatedUserCivility(id, t), value: id }));
  civilities.unshift({ value: '', label: '' });

  const B2BContactFormStep1Schema = Yup.object().shape({
    civility: Yup.string().required(),
    firstName: Yup.string().required(),
    lastName: Yup.string().required(),
    jobTitle: Yup.string().required(),
    email: Yup.string().email().required(),
    phone: Yup.string().phoneNumber().required(),
    mobilePhone: Yup.string().phoneNumber(),
  });

  return loadingCivility ? (
    <Loader />
  ) : (
    <Formik
      initialValues={{
        civility: '',
        lastName: '',
        firstName: '',
        jobTitle: '',
        email: '',
        phone: '',
        mobilePhone: '',
        ...initialValues,
      }}
      validationSchema={B2BContactFormStep1Schema}
      onSubmit={onSubmit}
    >
      {({ isSubmitting }) => (
        <Form className="contact-form">
          <div className="contact-form__main-inner">
            <div className="contact-form__main-column">
              <div className="contact-form__fieldset">
                <h3 className="contact-form__fieldset-legend">
                  <Trans>contact.form.personal-information</Trans>
                </h3>
                <Fields.FormikFastField
                  id="civility"
                  name="civility"
                  component={Fields.SelectField}
                  label={t('contact.form.label.civility')}
                  autoComplete={'contact honorific-prefix'}
                  required={true}
                  options={civilities}
                  className={'field-wrapper--limited'}
                />
                <Fields.FormikFastField
                  id="lastName"
                  name="lastName"
                  component={Fields.TextField}
                  label={t('contact.form.label.lastname')}
                  autoComplete={'contact family-name'}
                  required={true}
                />
                <Fields.FormikFastField
                  id="firstName"
                  name="firstName"
                  component={Fields.TextField}
                  label={t('contact.form.label.firstname')}
                  autoComplete={'contact given-name'}
                  required={true}
                />

                <Fields.FormikFastField
                  id="jobTitle"
                  name="jobTitle"
                  component={Fields.TextField}
                  label={t('contact.form.label.function')}
                  autoComplete={'contact organization-title'}
                  required={true}
                />
              </div>
            </div>
            <div className="contact-form__main-column">
              <div className="contact-form__fieldset">
                <h3 className="contact-form__fieldset-legend">
                  <Trans>contact.form.contact-information</Trans>
                </h3>
                <Fields.FormikFastField
                  id="email"
                  name="email"
                  component={Fields.TextField}
                  label={t('contact.form.label.email')}
                  type={'email'}
                  autoComplete={'contact email'}
                  required={true}
                />
                <BaseFormikField
                  component={Fields.PhoneNumberField}
                  id="phone"
                  name="phone"
                  className={'field--dotted'}
                  labels={[t('contact.form.label.country'), t('contact.form.label.phone1')]}
                  required={true}
                />
                <BaseFormikField
                  component={Fields.PhoneNumberField}
                  id="mobilePhone"
                  name="mobilePhone"
                  className={'field--dotted'}
                  labels={[t('contact.form.label.country'), t('contact.form.label.phone2')]}
                />
              </div>
            </div>
          </div>
          <div className="contact-form__buttons">
            <Button type="submit" disabled={isSubmitting} aria-label={t('contact.form.button.next')}>
              <Trans>contact.form.button.next</Trans>
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

Step1.propTypes = {
  initialValues: PropTypes.shape({
    civility: PropTypes.string,
    firstName: PropTypes.string,
    lastLame: PropTypes.string,
    email: PropTypes.string,
    jobTitle: PropTypes.string,
    phone: PropTypes.string,
    mobilePhone: PropTypes.string,
  }),
  onSubmit: PropTypes.func.isRequired,
};

export default Step1;
